import { getDocumentFollowUpCategories, getDocumentFollowUpEmployees } from 'api/requests/reporting-requests';
import { UPDropdownItem, UPMultipleSelectionDropdown } from 'components/dropdown';
import UPDropdownWithSearch from 'components/dropdown-with-search';
import UPMultipleSelectAutocomplete from 'components/dropdown-with-search/UPMultipleSelectAutocomplete';
import ErrorBar from 'components/error-bar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMPANY__COMPANY,
  DOCUMENTS__CATEGORY,
  GENERAL__SELECT,
  MENU__EMPLOYEES,
  REPORTING__DOCUMENT_FOLLOW_UP_MAX_COMPANIES,
} from 'translations/constants';
import { buildDocumentFollowUpEmployeeValue, MAX_DOCUMENT_FOLLOW_UP_COMPANIES } from '../helpers';
import { FilterItem, FiltersContainer } from '../styles';
import { BaseReportingFiltersProps } from '../types';

interface EmployeeOption extends UPDropdownItem {
  companyName: string;
}

interface DocumentFollowUpFiltersProps extends BaseReportingFiltersProps {
  referenceDate: string;
}

const DocumentFollowUpFilters = (props: DocumentFollowUpFiltersProps) => {
  const { filters, dropdownData, handleFilterChange, referenceDate } = props;

  const [t] = useTranslation();

  const { companiesDropdownItems } = dropdownData;
  const { selectedCompanies, selectedDocumentCategory } = filters;

  const [documentCategories, setDocumentCategories] = useState<UPDropdownItem[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
  const [capMessage, setCapMessage] = useState<string>('');

  const companiesKey = selectedCompanies.join('-');

  useEffect(() => {
    getDocumentFollowUpCategories()
      .then(response => {
        if (!response) return;

        setDocumentCategories(response.map(category => ({ value: category.value, label: t(category.label) })));
      })
      .catch(() => {
        setDocumentCategories([]);
      });
  }, [t]);

  useEffect(() => {
    const companyIds = companiesKey ? companiesKey.split('-') : [];

    if (companyIds.length === 0 || !referenceDate) {
      setEmployeeOptions([]);
      setLoadingEmployees(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingEmployees(true);

    getDocumentFollowUpEmployees({ CompanyIds: companyIds.map(Number), ReferenceDate: referenceDate })
      .then(response => {
        if (cancelled) return;

        const options: EmployeeOption[] = (response || []).map(({ companyId, ...employee }) => ({
          ...employee,
          value: buildDocumentFollowUpEmployeeValue(companyId, employee.value),
          companyName: companiesDropdownItems.find(company => `${company.value}` === `${companyId}`)?.label ?? '',
        }));

        options.sort((a, b) => a.companyName.localeCompare(b.companyName) || `${a.label}`.localeCompare(`${b.label}`));

        setEmployeeOptions(options);
        setLoadingEmployees(false);
      })
      .catch(() => {
        if (cancelled) return;

        setEmployeeOptions([]);
        setLoadingEmployees(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companiesKey, referenceDate, companiesDropdownItems]);

  const onCompaniesChange = (companies: string[]) => {
    if (companies.length > MAX_DOCUMENT_FOLLOW_UP_COMPANIES && companies.length > selectedCompanies.length) {
      setCapMessage(t(REPORTING__DOCUMENT_FOLLOW_UP_MAX_COMPANIES));
      return;
    }

    setCapMessage('');
    handleFilterChange('selectedCompanies', companies);
    handleFilterChange('selectedEmployees', []);
  };

  return (
    <>
      <FiltersContainer>
        <FilterItem>
          <UPMultipleSelectionDropdown
            items={companiesDropdownItems}
            value={selectedCompanies}
            dropdownLabel={t(COMPANY__COMPANY)}
            required
            handleChange={event => onCompaniesChange(event.target.value)}
          />
        </FilterItem>
        <FilterItem>
          <UPDropdownWithSearch
            items={documentCategories}
            value={selectedDocumentCategory}
            label={t(DOCUMENTS__CATEGORY)}
            required
            placeholder={t(GENERAL__SELECT)}
            onChange={category => handleFilterChange('selectedDocumentCategory', category)}
          />
        </FilterItem>
        <FilterItem sx={{ width: '500px' }}>
          <UPMultipleSelectAutocomplete
            key={`${companiesKey}_${referenceDate}`}
            items={employeeOptions}
            groupBy={(option: EmployeeOption) => option.companyName}
            handleChange={(_, newValue: UPDropdownItem[]) => {
              handleFilterChange(
                'selectedEmployees',
                newValue.map(item => item.value),
              );
            }}
            label={t(MENU__EMPLOYEES)}
            defaultValue={[]}
            placeholder={t(GENERAL__SELECT)}
            disabled={selectedCompanies.length === 0 || loadingEmployees}
          />
        </FilterItem>
      </FiltersContainer>
      {capMessage && <ErrorBar message={capMessage} />}
    </>
  );
};

export default DocumentFollowUpFilters;
